import { cloneDeep, get } from 'lodash';
import type { SupportedLanguage } from '../../../translate';
import { setText } from './epiHelpers';
import type {
  EpiComposition,
  EpiCustomSection,
  EpiDataSections,
  EpiPackagingBlock,
  EpiPredefinedEntry,
  EpiSection,
} from '../types/types';

// ============================================================================
// PATH-BASED DATA ACCESS
// ============================================================================

/**
 * Gets a section from a composition by its dot-separated path.
 * e.g. "sections.clinicalParticulars.sections.posology"
 */
export function getSectionAtPath(
  composition: EpiComposition,
  path: string
): EpiSection | undefined {
  return get(composition, path);
}

/**
 * Data path of the `sections` map owned by `parentPath`. The composition root
 * is addressed as 'root', which is not a real data path — its sections live
 * directly on the composition.
 */
export function sectionsPath(parentPath: string): string {
  return parentPath === 'root' ? 'sections' : `${parentPath}.sections`;
}

/**
 * Next print position for a packaging block, counted across **every**
 * repeatable key at that level rather than within one — printed PIs interleave
 * outer and blister blocks per product, so the three keys share one sequence.
 */
export function nextBlockOrder(
  composition: EpiComposition,
  parentPath: string
): number {
  const sections = get(composition, sectionsPath(parentPath)) as
    | Record<string, unknown>
    | undefined;
  const orders = Object.values(sections ?? {})
    .filter(Array.isArray)
    .flatMap(blocks =>
      (blocks as EpiPackagingBlock[])
        .filter(b => !b.toBeDeleted)
        .map(b => b.order ?? 0)
    );
  return orders.length === 0 ? 0 : Math.max(...orders) + 1;
}

/** Next print position among a section's custom children. */
export function nextCustomSectionOrder(
  composition: EpiComposition,
  parentPath: string
): number {
  const parent = get(composition, parentPath) as
    | { customSections?: EpiCustomSection[] }
    | undefined;
  const orders = (parent?.customSections ?? [])
    .filter(c => !c.toBeDeleted)
    .map(c => c.order ?? 0);
  return orders.length === 0 ? 0 : Math.max(...orders) + 1;
}

/** Where a custom section prints: its anchor among declared siblings, then `order`. */
type CustomPlace = Pick<EpiCustomSection, 'afterSequenceOrder' | 'order'>;

/**
 * Siblings in print order. Declared ones keep the order they are given in, the
 * QRD's. A custom section goes after the last declared one whose
 * `sequenceOrder` is at or below its anchor — `0` before all of them, an
 * absent anchor after all of them — and custom sections on one anchor follow
 * `order`. An anchor at a sibling that isn't present still lands between the
 * ones that are.
 */
export function orderSiblings<D, C>(
  declared: D[],
  custom: C[],
  sequenceOrderOf: (section: D) => number,
  placeOf: (section: C) => CustomPlace
): Array<D | C> {
  const anchor = (section: C) =>
    placeOf(section).afterSequenceOrder ?? Infinity;
  const pending = [...custom].sort(
    (a, b) => anchor(a) - anchor(b) || placeOf(a).order - placeOf(b).order
  );
  const ordered: Array<D | C> = [];
  for (const section of declared) {
    while (pending.length && anchor(pending[0]) < sequenceOrderOf(section)) {
      ordered.push(pending.shift()!);
    }
    ordered.push(section);
  }
  return [...ordered, ...pending];
}

/**
 * Moves a custom section one place up or down among all its siblings, declared
 * ones included, then restates every custom section of that parent from the
 * new order: anchored after the nearest declared section above it, or `0`, and
 * numbered within each anchor. Restating them all keeps the stored values
 * canonical whatever they held before. A parent with no declared children — a
 * custom H4 — numbers `order` alone. At either edge nothing moves.
 */
export function moveCustomSection(
  composition: EpiComposition,
  path: string,
  direction: -1 | 1
): EpiComposition {
  const match = /^(.*)\.customSections\[(\d+)\]$/.exec(path);
  if (!match) return composition;
  const [, parentPath, index] = match;
  const updated = cloneDeep(composition);
  const parent = get(updated, parentPath) as
    | { sections?: EpiDataSections; customSections?: EpiCustomSection[] }
    | undefined;
  const moving = parent?.customSections?.[Number(index)];
  if (!parent || !moving || moving.toBeDeleted) return composition;

  // Sorted rather than taken as stored: instance data is jsonb, whose key
  // order says nothing.
  const declared = Object.values(parent.sections ?? {})
    .flatMap(value =>
      Array.isArray(value)
        ? value.filter(block => !block.toBeDeleted)
        : value
          ? [value]
          : []
    )
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder);
  const custom = (parent.customSections ?? []).filter(c => !c.toBeDeleted);
  const siblings = orderSiblings(
    declared,
    custom,
    section => section.sequenceOrder,
    section => section
  );

  const from = siblings.indexOf(moving);
  const to = from + direction;
  if (to < 0 || to >= siblings.length) return composition;
  [siblings[from], siblings[to]] = [siblings[to], siblings[from]];

  let anchor = 0;
  const next = new Map<number, number>();
  for (const sibling of siblings) {
    if (!custom.includes(sibling as EpiCustomSection)) {
      anchor = (sibling as EpiSection).sequenceOrder;
      continue;
    }
    const section = sibling as EpiCustomSection;
    if (declared.length) section.afterSequenceOrder = anchor;
    section.order = next.get(anchor) ?? 0;
    next.set(anchor, section.order + 1);
  }
  return updated;
}

/** True for a repeatable-instance path such as "sections.blisterStrip[1]". */
export function isBlockPath(path: string): boolean {
  return /\[\d+\]$/.test(path) && !path.includes('customSections');
}

// ============================================================================
// PATH-BASED UPDATES
// ============================================================================

/**
 * Updates a section's content at the given path.
 */
export function updateSectionContentAtPath(
  composition: EpiComposition,
  path: string,
  lang: SupportedLanguage,
  content: string
): EpiComposition {
  const updated = cloneDeep(composition);
  const section = get(updated, path);
  if (section) {
    section.content = setText(section.content, lang, content);
  }
  return updated;
}

/**
 * Updates a section's title at the given path.
 */
export function updateSectionTitleAtPath(
  composition: EpiComposition,
  path: string,
  lang: SupportedLanguage,
  title: string
): EpiComposition {
  const updated = cloneDeep(composition);
  const section = get(updated, path);
  if (section) {
    section.title = setText(section.title, lang, title);
  }
  return updated;
}

/**
 * Adds an optional section (from schema) into the composition data.
 * The path should point to the parent section, and key is the property name to add.
 */
export function addOptionalSection(
  composition: EpiComposition,
  parentPath: string,
  key: string,
  sectionData: EpiSection
): EpiComposition {
  const updated = cloneDeep(composition);
  const parent = getSectionsOwner(updated, parentPath);
  if (parent) {
    if (!parent.sections) {
      parent.sections = {};
    }
    parent.sections[key] = sectionData;
  }
  return updated;
}

/**
 * Appends one instance of a repeatable section (the QRD Labelling packaging
 * blocks). The array is created when this is the first instance, which is the
 * case for the two optional blocks (blister/strip and small packaging).
 */
export function addPackagingBlock(
  composition: EpiComposition,
  parentPath: string,
  key: string,
  block: EpiPackagingBlock
): EpiComposition {
  const updated = cloneDeep(composition);
  const parent = getSectionsOwner(updated, parentPath);
  if (!parent) return updated;
  if (!parent.sections) {
    parent.sections = {};
  }
  const existing = parent.sections[key];
  parent.sections[key] = [...(Array.isArray(existing) ? existing : []), block];
  return updated;
}

/** Sets the nature/type on one repeatable block instance. */
export function updateNatureTypeAtPath(
  composition: EpiComposition,
  path: string,
  lang: SupportedLanguage,
  natureType: string
): EpiComposition {
  const updated = cloneDeep(composition);
  const block = get(updated, path);
  if (block) block.natureType = setText(block.natureType, lang, natureType);
  return updated;
}

/**
 * Adds a block right after the one printing at `afterOrder`, by moving every
 * later block one place down — across all repeatable keys of the level, since
 * they share one sequence. How a patient card follows its own strength's
 * packaging when one Labelling covers several strengths.
 */
export function insertPackagingBlockAfter(
  composition: EpiComposition,
  parentPath: string,
  key: string,
  block: EpiPackagingBlock,
  afterOrder: number
): EpiComposition {
  const updated = cloneDeep(composition);
  const sections = get(updated, sectionsPath(parentPath)) as
    | Record<string, unknown>
    | undefined;
  Object.values(sections ?? {})
    .filter(Array.isArray)
    .flatMap(blocks => blocks as EpiPackagingBlock[])
    .filter(b => !b.toBeDeleted && (b.order ?? 0) > afterOrder)
    .forEach(b => (b.order += 1));
  return addPackagingBlock(updated, parentPath, key, {
    ...block,
    order: afterOrder + 1,
  });
}

/**
 * Soft-deletes one instance of a repeatable section. Blocks live in an array
 * the BE merges by `id`, so the item has to stay in the payload flagged rather
 * than be spliced out — same rule as customSections.
 */
export function removePackagingBlock(
  composition: EpiComposition,
  path: string
): EpiComposition {
  const updated = cloneDeep(composition);
  const block = get(updated, path);
  if (block) block.toBeDeleted = true;
  return updated;
}

/**
 * The section that owns a `sections` map — the composition itself for the
 * root, a nested section otherwise.
 */
function getSectionsOwner(
  composition: EpiComposition,
  parentPath: string
): { sections?: any } | undefined {
  return parentPath === 'root' ? composition : get(composition, parentPath);
}

/**
 * Adds a custom section to a section that allows it.
 * The path should point to the section with allowCustom.
 */
export function addCustomSection(
  composition: EpiComposition,
  path: string,
  customSection: EpiCustomSection
): EpiComposition {
  const updated = cloneDeep(composition);
  const section = get(updated, path);
  if (section) {
    if (!section.customSections) {
      section.customSections = [];
    }
    section.customSections.push(customSection);
  }
  return updated;
}

/**
 * Adds a predefined custom section at the place its entry gives it: on the
 * entry's anchor, after the predefined sections the list puts before it and
 * before those it puts after, then that anchor's group renumbered. `entries`
 * is the host's list, in print order.
 *
 * An entry deleted earlier in this session is revived rather than added
 * again: its item is still in state, flagged, and two items with one key in a
 * payload would break the host's `x-unique`. It comes back as new — the
 * section's title and content, and none of its old children.
 */
export function addPredefinedSection(
  composition: EpiComposition,
  hostPath: string,
  entries: EpiPredefinedEntry[],
  section: EpiCustomSection
): EpiComposition {
  const updated = cloneDeep(composition);
  const host = get(updated, hostPath) as
    | { customSections?: EpiCustomSection[] }
    | undefined;
  if (!host) return composition;
  host.customSections ??= [];

  const deleted = host.customSections.find(
    c => c.toBeDeleted && c.predefinedKey === section.predefinedKey
  );
  let added: EpiCustomSection = section;
  if (deleted) {
    delete deleted.toBeDeleted;
    deleted.customSections?.forEach(child => (child.toBeDeleted = true));
    Object.assign(deleted, { ...section, id: deleted.id });
    added = deleted;
  } else {
    host.customSections.push(section);
  }

  const anchor = (c: EpiCustomSection) => c.afterSequenceOrder ?? Infinity;
  const rank = (c: EpiCustomSection) =>
    entries.findIndex(entry => entry.key === c.predefinedKey);
  const group = host.customSections
    .filter(c => !c.toBeDeleted && c !== added && anchor(c) === anchor(added))
    .sort((a, b) => a.order - b.order);
  const at = group.findIndex(c => !!c.predefinedKey && rank(c) > rank(added));
  group.splice(at < 0 ? group.length : at, 0, added);
  group.forEach((c, i) => (c.order = i));
  return updated;
}

/**
 * Keeps an imported heading as a free custom section. `null`, not `delete`:
 * the merge keeps a key the payload leaves out.
 */
export function keepCustomSection(
  composition: EpiComposition,
  path: string
): EpiComposition {
  const updated = cloneDeep(composition);
  const section = get(updated, path) as EpiCustomSection | undefined;
  if (section) section.unmatched = null;
  return updated;
}

/**
 * Removes an optional section from the composition data.
 * The path should point to the parent section, and key is the property name to remove.
 */
export function removeOptionalSection(
  composition: EpiComposition,
  parentPath: string,
  key: string
): EpiComposition {
  const updated = cloneDeep(composition);
  const parent = get(updated, parentPath);
  if (parent?.[key]) {
    // `null`, not `delete`: the BE merges objects, so a key left out of the
    // payload is kept as it was stored, and only an explicit `null` removes it
    // (`toBeDeleted` is the array-element signal, and ignored on an object).
    parent[key] = null;
  }
  return updated;
}

/**
 * Removes a custom section by its index in the customSections array.
 * The path should point to the section containing the customSections.
 */
export function removeCustomSection(
  composition: EpiComposition,
  path: string,
  customSectionId: string
): EpiComposition {
  const updated = cloneDeep(composition);
  const section = get(updated, path);
  if (section?.customSections) {
    const cs = section.customSections.find(
      (c: any) => c.id === customSectionId
    );
    if (cs) cs.toBeDeleted = true;
  }
  return updated;
}
